/**
 * Datos completos de productos Retail
 * Incluye descripciones, ingredientes, formatos e información detallada
 *
 * Los textos traducibles son objetos `{ es, en, fr, pt }` (ver
 * `i18n/localizeContent`); los ids, slugs, imágenes, nombres de producto y
 * formatos son valores simples. El texto de `formatDisplay`/`formats` se
 * localiza al mostrarlo (`components/products/productFormat`).
 * Los ingredientes se traducen fielmente (etiquetado legal): no reescribir.
 */

const INGREDIENTES_KETCHUP = {
  es: 'Concentrado de tomate, azúcar, vinagre, sal, almidón modificado de maíz, estabilizantes (goma guar, goma garrofín, pectinas), conservador (benzoato sódico) y aromas.',
  en: 'Tomato concentrate, sugar, vinegar, salt, modified maize starch, stabilisers (guar gum, locust bean gum, pectins), preservative (sodium benzoate) and flavourings.',
  fr: 'Concentré de tomate, sucre, vinaigre, sel, amidon modifié de maïs, stabilisants (gomme guar, gomme de caroube, pectines), conservateur (benzoate de sodium) et arômes.',
  pt: 'Concentrado de tomate, açúcar, vinagre, sal, amido modificado de milho, estabilizadores (goma guar, goma de alfarroba, pectinas), conservante (benzoato de sódio) e aromas.',
};

export const retailProductsData = {
  // CATEGORÍA 1: KETCHUP EN CRISTAL
  ketchupCristal: [
    {
      id: 'ketchup-cristal',
      name: 'Ketchup Intercasa',
      shortName: 'Ketchup',
      category: {
        es: 'Ketchup en Cristal',
        en: 'Ketchup in Glass',
        fr: 'Ketchup en verre',
        pt: 'Ketchup em vidro',
      },
      categorySlug: 'ketchup-cristal',
      shortDescription: {
        es: 'Ketchup clásico elaborado en Canarias con tomates seleccionados.',
        en: 'Classic ketchup made in the Canary Islands from carefully selected tomatoes.',
        fr: 'Ketchup classique élaboré aux Canaries avec des tomates soigneusement sélectionnées.',
        pt: 'Ketchup clássico produzido nas Canárias com tomates selecionados.',
      },
      description: {
        es: 'Nuestro ketchup clásico, elaborado en Canarias con tomates seleccionados, tiene un sabor equilibrado y familiar que acompaña perfectamente cualquier plato del día a día',
        en: 'Our classic ketchup, made in the Canary Islands from carefully selected tomatoes, has a balanced, familiar flavour that goes perfectly with any everyday dish.',
        fr: 'Notre ketchup classique, élaboré aux Canaries avec des tomates soigneusement sélectionnées, offre une saveur équilibrée et familière qui accompagne à merveille tous les plats du quotidien.',
        pt: 'O nosso ketchup clássico, produzido nas Canárias com tomates selecionados, tem um sabor equilibrado e familiar que acompanha na perfeição qualquer prato do dia a dia.',
      },
      ingredients: INGREDIENTES_KETCHUP,
      formats: ['320g', '530g'],
      formatDisplay: 'CRISTAL 320g | 530g',
      idealFor: {
        es: 'Bocadillos, papas fritas, carnes, pasta y cualquier receta rápida en casa.',
        en: 'Sandwiches, chips, meat, pasta and any quick recipe at home.',
        fr: 'Sandwichs, frites, viandes, pâtes et toutes vos recettes rapides à la maison.',
        pt: 'Sandes, batatas fritas, carnes, massa e qualquer receita rápida em casa.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Ketchup_cristal_320gr.webp',
      formatImages: {
        '320g': '/images/productos/retail/Ketchup_cristal_320gr.webp',
        '530g': '/images/productos/retail/Ketchup_cristal_530gr.webp'
      }
    },
    {
      id: 'ketchup-zero-cristal',
      name: 'Ketchup Zero',
      shortName: 'Ketchup Zero',
      category: {
        es: 'Ketchup en Cristal',
        en: 'Ketchup in Glass',
        fr: 'Ketchup en verre',
        pt: 'Ketchup em vidro',
      },
      categorySlug: 'ketchup-cristal',
      shortDescription: {
        es: 'Sin azúcares añadidos, ligero y con el sabor Intercasa.',
        en: 'No added sugars, light, and with the Intercasa taste.',
        fr: 'Sans sucres ajoutés, léger et avec le goût Intercasa.',
        pt: 'Sem açúcares adicionados, leve e com o sabor Intercasa.',
      },
      description: {
        es: 'La opción sin azúcares añadidos para quienes buscan un ketchup más ligero sin renunciar al sabor tan característico de Intercasa',
        en: 'The no-added-sugar option for anyone looking for a lighter ketchup without giving up the distinctive Intercasa flavour.',
        fr: 'L’option sans sucres ajoutés pour ceux qui recherchent un ketchup plus léger sans renoncer à la saveur si caractéristique d’Intercasa.',
        pt: 'A opção sem açúcares adicionados para quem procura um ketchup mais leve sem abdicar do sabor tão característico da Intercasa.',
      },
      ingredients: {
        es: 'Concentrado de tomate, puré de manzana, edulcorante (maltitol), vinagre de alcohol, sal, almidón modificado de maíz, estabilizante (goma guar), conservador (benzoato sódico), edulcorante (sucralosa) y aromas.',
        en: 'Tomato concentrate, apple purée, sweetener (maltitol), spirit vinegar, salt, modified maize starch, stabiliser (guar gum), preservative (sodium benzoate), sweetener (sucralose) and flavourings.',
        fr: 'Concentré de tomate, purée de pomme, édulcorant (maltitol), vinaigre d’alcool, sel, amidon modifié de maïs, stabilisant (gomme guar), conservateur (benzoate de sodium), édulcorant (sucralose) et arômes.',
        pt: 'Concentrado de tomate, puré de maçã, edulcorante (maltitol), vinagre de álcool, sal, amido modificado de milho, estabilizador (goma guar), conservante (benzoato de sódio), edulcorante (sucralose) e aromas.',
      },
      formats: ['320g'],
      formatDisplay: 'CRISTAL 320g',
      idealFor: {
        es: 'Comidas ligeras, recetas saludables, acompañar sin remordimientos.',
        en: 'Light meals, healthy recipes, and a guilt-free accompaniment.',
        fr: 'Repas légers, recettes saines, un accompagnement sans culpabilité.',
        pt: 'Refeições leves, receitas saudáveis e um acompanhamento sem remorsos.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Ketchup_cristal_zero_320gr.webp'
    },
    {
      id: 'ketchup-picante-cristal',
      name: 'Ketchup Picante',
      shortName: 'Ketchup Picante',
      category: {
        es: 'Ketchup en Cristal',
        en: 'Ketchup in Glass',
        fr: 'Ketchup en verre',
        pt: 'Ketchup em vidro',
      },
      categorySlug: 'ketchup-cristal',
      shortDescription: {
        es: 'El ketchup de siempre con un toque picante que le da carácter.',
        en: 'The ketchup you know and love, with a spicy kick that gives it character.',
        fr: 'Le ketchup de toujours avec une touche épicée qui lui donne du caractère.',
        pt: 'O ketchup de sempre com um toque picante que lhe dá caráter.',
      },
      description: {
        es: 'Nuestro ketchup de siempre, pero con un toque picante que le da carácter. Una receta pensada para quienes disfrutan de un poco más de intensidad en cada bocado',
        en: 'Our much-loved ketchup, with a spicy kick that gives it character. A recipe made for those who enjoy a little more intensity in every bite.',
        fr: 'Notre ketchup de toujours, avec une touche épicée qui lui donne du caractère. Une recette pensée pour ceux qui aiment un peu plus d’intensité à chaque bouchée.',
        pt: 'O nosso ketchup de sempre, mas com um toque picante que lhe dá caráter. Uma receita pensada para quem gosta de um pouco mais de intensidade em cada dentada.',
      },
      ingredients: INGREDIENTES_KETCHUP,
      formats: ['320g'],
      formatDisplay: 'CRISTAL 320g',
      idealFor: {
        es: 'Carnes, papas, bocadillos y platos que piden un punto atrevido.',
        en: 'Meat, potatoes, sandwiches and dishes that call for a bolder touch.',
        fr: 'Viandes, pommes de terre, sandwichs et plats qui réclament une pointe d’audace.',
        pt: 'Carnes, batatas, sandes e pratos que pedem um toque mais ousado.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Ketchup_picante_cristal_320gr.webp'
    }
  ],

  // CATEGORÍA 2: LÍNEA PE
  lineaPE: [
    {
      id: 'ketchup-pe',
      name: 'Ketchup Intercasa PE',
      shortName: 'Ketchup',
      category: {
        es: 'Línea PE',
        en: 'PE Range',
        fr: 'Gamme PE',
        pt: 'Linha PE',
      },
      categorySlug: 'linea-pe',
      shortDescription: {
        es: 'Ketchup práctico con el sabor equilibrado de siempre.',
        en: 'Practical ketchup with the same balanced flavour as always.',
        fr: 'Un ketchup pratique, à la saveur équilibrée de toujours.',
        pt: 'Ketchup prático com o sabor equilibrado de sempre.',
      },
      description: {
        es: 'Un ketchup práctico y cómodo para el día a día, con el sabor equilibrado de los tomates seleccionados y el formato ideal para tener siempre a mano en casa.',
        en: 'A practical, convenient ketchup for everyday use, with the balanced flavour of carefully selected tomatoes and the ideal format to keep close at hand at home.',
        fr: 'Un ketchup pratique et facile à utiliser au quotidien, avec la saveur équilibrée de tomates sélectionnées et le format idéal pour l’avoir toujours sous la main à la maison.',
        pt: 'Um ketchup prático e cómodo para o dia a dia, com o sabor equilibrado de tomates selecionados e o formato ideal para ter sempre à mão em casa.',
      },
      ingredients: {
        es: 'Tomates (192g de tomate por 100g de kétchup), azúcar, vinagre, sal, almidón modificado de maíz, estabilizantes (goma guar, goma garrofín, pectinas), conservador (benzoato sódico) y aromas.',
        en: 'Tomatoes (192g of tomato per 100g of ketchup), sugar, vinegar, salt, modified maize starch, stabilisers (guar gum, locust bean gum, pectins), preservative (sodium benzoate) and flavourings.',
        fr: 'Tomates (192\u00a0g de tomate pour 100\u00a0g de ketchup), sucre, vinaigre, sel, amidon modifié de maïs, stabilisants (gomme guar, gomme de caroube, pectines), conservateur (benzoate de sodium) et arômes.',
        pt: 'Tomates (192\u00a0g de tomate por 100\u00a0g de ketchup), açúcar, vinagre, sal, amido modificado de milho, estabilizadores (goma guar, goma de alfarroba, pectinas), conservante (benzoato de sódio) e aromas.',
      },
      formats: ['300g PE', '450g PE'],
      formatDisplay: 'PE 300g | 450g',
      idealFor: {
        es: 'Bocadillos, papas, carnes a la plancha y comidas rápidas en familia.',
        en: 'Sandwiches, potatoes, grilled meat and quick family meals.',
        fr: 'Sandwichs, pommes de terre, viandes à la plancha et repas rapides en famille.',
        pt: 'Sandes, batatas, carnes na chapa e refeições rápidas em família.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Ketchup_PE_300gr.webp',
      formatImages: {
        '300g PE': '/images/productos/retail/Ketchup_PE_300gr.webp',
        '450g PE': '/images/productos/retail/Ketchup_PE_450gr.webp'
      }
    },
    {
      id: 'mostaza-dulce-original-pe',
      name: 'Mostaza Dulce Original',
      shortName: 'Mostaza Dulce Original',
      category: {
        es: 'Línea PE',
        en: 'PE Range',
        fr: 'Gamme PE',
        pt: 'Linha PE',
      },
      categorySlug: 'linea-pe',
      shortDescription: {
        es: 'Receta clásica de mostaza dulce, suave y equilibrada.',
        en: 'Classic sweet mustard recipe, mild and balanced.',
        fr: 'Recette classique de moutarde sucrée, douce et équilibrée.',
        pt: 'Receita clássica de mostarda doce, suave e equilibrada.',
      },
      description: {
        es: 'Nuestra receta clásica de mostaza dulce, con un sabor suave y equilibrado que acompaña perfectamente carnes, salchichas o bocadillos',
        en: 'Our classic sweet mustard recipe, with a mild, balanced flavour that pairs perfectly with meat, sausages or sandwiches.',
        fr: 'Notre recette classique de moutarde sucrée, à la saveur douce et équilibrée, qui accompagne parfaitement viandes, saucisses ou sandwichs.',
        pt: 'A nossa receita clássica de mostarda doce, com um sabor suave e equilibrado que acompanha na perfeição carnes, salsichas ou sandes.',
      },
      ingredients: {
        es: 'Agua, azúcar, vinagre de alcohol, harina de mostaza, aceite de girasol, sal, especias, estabilizantes (goma guar, goma xantana, goma garrofín, pectinas), aromas (apio) y conservadores (benzoato de sodio y sorbato de potasio).',
        en: 'Water, sugar, spirit vinegar, mustard flour, sunflower oil, salt, spices, stabilisers (guar gum, xanthan gum, locust bean gum, pectins), flavourings (celery) and preservatives (sodium benzoate and potassium sorbate).',
        fr: 'Eau, sucre, vinaigre d’alcool, farine de moutarde, huile de tournesol, sel, épices, stabilisants (gomme guar, gomme xanthane, gomme de caroube, pectines), arômes (céleri) et conservateurs (benzoate de sodium et sorbate de potassium).',
        pt: 'Água, açúcar, vinagre de álcool, farinha de mostarda, óleo de girassol, sal, especiarias, estabilizadores (goma guar, goma xantana, goma de alfarroba, pectinas), aromas (aipo) e conservantes (benzoato de sódio e sorbato de potássio).',
      },
      formats: ['300g PE', '450g PE'],
      formatDisplay: 'PE 300g | 450g',
      idealFor: {
        es: 'Perritos calientes, bocadillos, carnes y recetas que necesitan un toque dulce.',
        en: 'Hot dogs, sandwiches, meat and recipes that need a touch of sweetness.',
        fr: 'Hot-dogs, sandwichs, viandes et recettes qui demandent une pointe de douceur.',
        pt: 'Cachorros-quentes, sandes, carnes e receitas que pedem um toque doce.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Mostaza_dulce_original_PE_300g.webp',
      formatImages: {
        '300g PE': '/images/productos/retail/Mostaza_dulce_original_PE_300g.webp',
        '450g PE': '/images/productos/retail/Mostaza_dulce_original_PE_300g.webp'
      }
    },
    {
      id: 'mostaza-dulce-pe',
      name: 'Mostaza Dulce',
      shortName: 'Mostaza Dulce',
      category: {
        es: 'Línea PE',
        en: 'PE Range',
        fr: 'Gamme PE',
        pt: 'Linha PE',
      },
      categorySlug: 'linea-pe',
      shortDescription: {
        es: 'Mostaza cremosa con toque amable y ligeramente especiado.',
        en: 'Creamy mustard with a gentle, lightly spiced touch.',
        fr: 'Moutarde crémeuse, à la touche délicate et légèrement relevée.',
        pt: 'Mostarda cremosa com um toque suave e ligeiramente especiado.',
      },
      description: {
        es: 'Una versión más actual de nuestra mostaza dulce, con una textura cremosa y un sabor pensado para quienes disfrutan de un toque amable y ligeramente especiado.',
        en: 'A more modern take on our sweet mustard, with a creamy texture and a flavour made for those who enjoy a gentle, lightly spiced touch.',
        fr: 'Une version plus actuelle de notre moutarde sucrée, à la texture crémeuse et à la saveur pensée pour ceux qui apprécient une touche délicate et légèrement relevée.',
        pt: 'Uma versão mais atual da nossa mostarda doce, com uma textura cremosa e um sabor pensado para quem gosta de um toque suave e ligeiramente especiado.',
      },
      ingredients: {
        es: 'Agua, azúcar, vinagre de alochol, harina de mostaza, estabilizantes (goma guar goma xantana y almidón modificado de maíz), sal, aceite de girasol, conservador (metabisulfito potásico y sorbato potásico) colorante (caramelo) y especias. Contiene dióxido de azufre y sulfitos en concentraciones superiores a 10mg/kg.',
        en: 'Water, sugar, spirit vinegar, mustard flour, stabilisers (guar gum, xanthan gum and modified maize starch), salt, sunflower oil, preservatives (potassium metabisulphite and potassium sorbate), colour (caramel) and spices. Contains sulphur dioxide and sulphites at concentrations above 10mg/kg.',
        fr: 'Eau, sucre, vinaigre d’alcool, farine de moutarde, stabilisants (gomme guar, gomme xanthane et amidon modifié de maïs), sel, huile de tournesol, conservateurs (métabisulfite de potassium et sorbate de potassium), colorant (caramel) et épices. Contient du dioxyde de soufre et des sulfites en concentrations supérieures à 10\u00a0mg/kg.',
        pt: 'Água, açúcar, vinagre de álcool, farinha de mostarda, estabilizadores (goma guar, goma xantana e amido modificado de milho), sal, óleo de girassol, conservantes (metabissulfito de potássio e sorbato de potássio), corante (caramelo) e especiarias. Contém dióxido de enxofre e sulfitos em concentrações superiores a 10\u00a0mg/kg.',
      },
      formats: ['300g PE', '450g PE'],
      formatDisplay: 'PE 300g | 450g',
      idealFor: {
        es: 'Carnes blancas, papas, sándwiches y elaboraciones sencillas.',
        en: 'White meat, potatoes, sandwiches and simple dishes.',
        fr: 'Viandes blanches, pommes de terre, sandwichs et préparations simples.',
        pt: 'Carnes brancas, batatas, sandes e preparações simples.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Mostaza_dulce_PE_300g.webp',
      formatImages: {
        '300g PE': '/images/productos/retail/Mostaza_dulce_PE_300g.webp',
        '450g PE': '/images/productos/retail/Mostaza_dulce_PE_450g.webp'
      }
    },
    {
      id: 'salsa-barbacoa-pe',
      name: 'Salsa Barbacoa',
      shortName: 'Barbacoa',
      category: {
        es: 'Línea PE',
        en: 'PE Range',
        fr: 'Gamme PE',
        pt: 'Linha PE',
      },
      categorySlug: 'linea-pe',
      shortDescription: {
        es: 'Sabor intenso y ahumado, ideal para carnes y parrillas.',
        en: 'Intense, smoky flavour, ideal for meat and barbecues.',
        fr: 'Saveur intense et fumée, idéale pour les viandes et les grillades.',
        pt: 'Sabor intenso e fumado, ideal para carnes e grelhados.',
      },
      description: {
        es: 'Una salsa barbacoa de sabor intenso y ligeramente ahumado, ideal para darle carácter a carnes, hamburguesas y recetas a la parrilla',
        en: 'A barbecue sauce with an intense, lightly smoky flavour, ideal for adding character to meat, burgers and grilled recipes.',
        fr: 'Une sauce barbecue à la saveur intense et légèrement fumée, idéale pour donner du caractère aux viandes, aux hamburgers et aux recettes grillées.',
        pt: 'Um molho barbecue de sabor intenso e ligeiramente fumado, ideal para dar caráter a carnes, hambúrgueres e receitas na grelha.',
      },
      ingredients: {
        es: 'Concentrado de tomate, azúcar, jarabe de melaza, vinagre de alcohol, sal, almidón modificado de maíz, aroma de humo, estabilizante (goma guar), conservador (benzoato sódico), especias y aromas.',
        en: 'Tomato concentrate, sugar, molasses syrup, spirit vinegar, salt, modified maize starch, smoke flavouring, stabiliser (guar gum), preservative (sodium benzoate), spices and flavourings.',
        fr: 'Concentré de tomate, sucre, sirop de mélasse, vinaigre d’alcool, sel, amidon modifié de maïs, arôme de fumée, stabilisant (gomme guar), conservateur (benzoate de sodium), épices et arômes.',
        pt: 'Concentrado de tomate, açúcar, xarope de melaço, vinagre de álcool, sal, amido modificado de milho, aroma de fumo, estabilizador (goma guar), conservante (benzoato de sódio), especiarias e aromas.',
      },
      formats: ['300g PE', '450g PE'],
      formatDisplay: 'PE 300g | 450g',
      idealFor: {
        es: 'Carnes a la parrilla, hamburguesas, papas y marinados.',
        en: 'Grilled meat, burgers, potatoes and marinades.',
        fr: 'Viandes grillées, hamburgers, pommes de terre et marinades.',
        pt: 'Carnes na grelha, hambúrgueres, batatas e marinadas.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Barbacoa_PE_300g.webp',
      formatImages: {
        '300g PE': '/images/productos/retail/Barbacoa_PE_300g.webp',
        '450g PE': '/images/productos/retail/Barbacoa_PE_450g.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Barbacoa.webp'
    }
  ],

  // CATEGORÍA 3: ALIOLI Y MAYONESA
  alioliMayonesa: [
    {
      id: 'mayonesa-pe',
      name: 'Mayonesa Intercasa',
      shortName: 'Mayonesa',
      category: {
        es: 'Alioli y Mayonesa',
        en: 'Alioli and Mayonnaise',
        fr: 'Alioli et mayonnaise',
        pt: 'Alioli e maionese',
      },
      categorySlug: 'alioli-mayonesa',
      shortDescription: {
        es: 'Mayonesa suave y cremosa, elaborada en Canarias.',
        en: 'Smooth, creamy mayonnaise made in the Canary Islands.',
        fr: 'Mayonnaise onctueuse et crémeuse, élaborée aux Canaries.',
        pt: 'Maionese suave e cremosa, produzida nas Canárias.',
      },
      description: {
        es: 'Una mayonesa suave y cremosa, elaborada en Canarias con una textura perfecta para acompañar platos del día a día y realzar su sabor sin resultar pesada',
        en: 'A smooth, creamy mayonnaise made in the Canary Islands, with the perfect texture to accompany everyday dishes and enhance their flavour without being heavy.',
        fr: 'Une mayonnaise onctueuse et crémeuse, élaborée aux Canaries, à la texture parfaite pour accompagner les plats du quotidien et rehausser leur saveur sans les alourdir.',
        pt: 'Uma maionese suave e cremosa, produzida nas Canárias, com a textura perfeita para acompanhar os pratos do dia a dia e realçar o seu sabor sem pesar.',
      },
      ingredients: {
        es: 'Aceite de girasol (64%), agua, huevo y yema de huevo (6,5%), vinagre de vino blanco, azúcar, sal, almidón modificado de maíz, estabilizante (goma garrofín), zumo de limón, conservador (sorbato potásico), mostaza, acidulante (ácido cítrico), antioxidante (EDTA), colorante (betacaroteno) y aromas.',
        en: 'Sunflower oil (64%), water, egg and egg yolk (6.5%), white wine vinegar, sugar, salt, modified maize starch, stabiliser (locust bean gum), lemon juice, preservative (potassium sorbate), mustard, acidifier (citric acid), antioxidant (EDTA), colour (beta-carotene) and flavourings.',
        fr: 'Huile de tournesol (64\u00a0%), eau, œuf et jaune d’œuf (6,5\u00a0%), vinaigre de vin blanc, sucre, sel, amidon modifié de maïs, stabilisant (gomme de caroube), jus de citron, conservateur (sorbate de potassium), moutarde, acidifiant (acide citrique), antioxydant (EDTA), colorant (bêta-carotène) et arômes.',
        pt: 'Óleo de girassol (64%), água, ovo e gema de ovo (6,5%), vinagre de vinho branco, açúcar, sal, amido modificado de milho, estabilizador (goma de alfarroba), sumo de limão, conservante (sorbato de potássio), mostarda, acidificante (ácido cítrico), antioxidante (EDTA), corante (betacaroteno) e aromas.',
      },
      formats: ['430ml PE'],
      formatDisplay: 'PE 430ml',
      idealFor: {
        es: 'Papas, ensaladas, bocadillos, carnes frías y recetas rápidas para compartir.',
        en: 'Potatoes, salads, sandwiches, cold cuts and quick recipes to share.',
        fr: 'Pommes de terre, salades, sandwichs, charcuteries et recettes rapides à partager.',
        pt: 'Batatas, saladas, sandes, carnes frias e receitas rápidas para partilhar.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Mayonesa_PET_300ml.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Mayonesa.webp'
    },
    {
      id: 'alioli-receta-canaria-pe',
      name: 'Alioli Receta Canaria',
      shortName: 'Alioli Receta Canaria',
      category: {
        es: 'Alioli y Mayonesa',
        en: 'Alioli and Mayonnaise',
        fr: 'Alioli et mayonnaise',
        pt: 'Alioli e maionese',
      },
      categorySlug: 'alioli-mayonesa',
      shortDescription: {
        es: 'Receta canaria tradicional con sabor intenso y textura suave.',
        en: 'Traditional Canarian recipe with an intense flavour and smooth texture.',
        fr: 'Recette canarienne traditionnelle, à la saveur intense et à la texture douce.',
        pt: 'Receita canária tradicional com sabor intenso e textura suave.',
      },
      description: {
        es: 'Nuestro alioli tradicional, elaborado con una receta canaria que combina un sabor intenso y una textura suave, ideal para acompañar platos caseros con un toque auténtico.',
        en: 'Our traditional alioli (garlic mayonnaise), made to a Canarian recipe that combines an intense flavour with a smooth texture, ideal for accompanying home-cooked dishes with an authentic touch.',
        fr: 'Notre alioli traditionnel (mayonnaise à l’ail), élaboré selon une recette canarienne qui associe une saveur intense et une texture douce, idéal pour accompagner les plats maison avec une touche d’authenticité.',
        pt: 'O nosso alioli tradicional (maionese de alho), elaborado com uma receita canária que combina um sabor intenso e uma textura suave, ideal para acompanhar pratos caseiros com um toque autêntico.',
      },
      ingredients: {
        es: 'Aceite de girasol (64%), agua, huevo y yema de huevo(6,5%), ajo natural (4,8%), vinagre de vino blanco, azúcar, perejil, sal, almidón modificado de maíz, estabilizante (goma garrofín), zumo de limón, conservador (sorbato potásico), mostaza, acidulante (ácido cítrico), antioxidante (EDTA), colorante (betacaroteno) y aromas.',
        en: 'Sunflower oil (64%), water, egg and egg yolk (6.5%), natural garlic (4.8%), white wine vinegar, sugar, parsley, salt, modified maize starch, stabiliser (locust bean gum), lemon juice, preservative (potassium sorbate), mustard, acidifier (citric acid), antioxidant (EDTA), colour (beta-carotene) and flavourings.',
        fr: 'Huile de tournesol (64\u00a0%), eau, œuf et jaune d’œuf (6,5\u00a0%), ail naturel (4,8\u00a0%), vinaigre de vin blanc, sucre, persil, sel, amidon modifié de maïs, stabilisant (gomme de caroube), jus de citron, conservateur (sorbate de potassium), moutarde, acidifiant (acide citrique), antioxydant (EDTA), colorant (bêta-carotène) et arômes.',
        pt: 'Óleo de girassol (64%), água, ovo e gema de ovo (6,5%), alho natural (4,8%), vinagre de vinho branco, açúcar, salsa, sal, amido modificado de milho, estabilizador (goma de alfarroba), sumo de limão, conservante (sorbato de potássio), mostarda, acidificante (ácido cítrico), antioxidante (EDTA), corante (betacaroteno) e aromas.',
      },
      formats: ['300ml PE', '430ml PE'],
      formatDisplay: 'PE 300ml | 430ml',
      idealFor: {
        es: 'Papas arrugadas, carnes, pescados, bocadillos y snacks.',
        en: 'Papas arrugadas (Canarian wrinkled potatoes), meat, fish, sandwiches and snacks.',
        fr: 'Papas arrugadas (pommes de terre ridées canariennes), viandes, poissons, sandwichs et snacks.',
        pt: 'Papas arrugadas (batatas enrugadas canárias), carnes, peixes, sandes e snacks.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Aliol_PET_300ml.webp',
      formatImages: {
        '300ml PE': '/images/productos/retail/Aliol_PET_300ml.webp',
        '430ml PE': '/images/productos/retail/Alioli_PET_430ml.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/Alioli.webp'
    }
  ],

  // CATEGORÍA 4: TOMATES EN CONSERVA
  tomates: [
    {
      id: 'tomate-entero-pelado',
      name: 'Tomate Entero Pelado',
      shortName: 'Tomate Entero Pelado',
      category: {
        es: 'Tomates en Conserva',
        en: 'Canned Tomatoes',
        fr: 'Tomates en conserve',
        pt: 'Tomates em conserva',
      },
      categorySlug: 'tomates-conserva',
      shortDescription: {
        es: 'Tomates enteros con jugo natural para salsas caseras.',
        en: 'Whole tomatoes in natural juice for homemade sauces.',
        fr: 'Tomates entières dans leur jus naturel pour des sauces maison.',
        pt: 'Tomates inteiros com sumo natural para molhos caseiros.',
      },
      description: {
        es: 'Tomates enteros pelados y envasados con su jugo natural, ideales para preparar salsas caseras y recetas tradicionales con un sabor auténtico.',
        en: 'Whole peeled tomatoes packed in their natural juice, ideal for making homemade sauces and traditional recipes with an authentic flavour.',
        fr: 'Tomates entières pelées et conditionnées dans leur jus naturel, idéales pour préparer des sauces maison et des recettes traditionnelles à la saveur authentique.',
        pt: 'Tomates inteiros pelados e embalados no seu sumo natural, ideais para preparar molhos caseiros e receitas tradicionais com um sabor autêntico.',
      },
      ingredients: {
        es: 'Tomate, jugo de tomate, sal, acidulante: E330 y endurecedor: E509.',
        en: 'Tomato, tomato juice, salt, acidifier: E330 and firming agent: E509.',
        fr: 'Tomate, jus de tomate, sel, acidifiant : E330 et raffermissant : E509.',
        pt: 'Tomate, sumo de tomate, sal, acidificante: E330 e endurecedor: E509.',
      },
      formats: ['380g', '765g'],
      formatDisplay: '380g | 765g',
      idealFor: {
        es: 'Salsas, guisos, recetas caseras y platos que requieren tomate natural.',
        en: 'Sauces, stews, home cooking and dishes that call for natural tomato.',
        fr: 'Sauces, ragoûts, recettes maison et plats qui demandent de la tomate naturelle.',
        pt: 'Molhos, guisados, receitas caseiras e pratos que pedem tomate natural.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Tomate_pelado_lata_380g.webp',
      formatImages: {
        '380g': '/images/productos/retail/Tomate_pelado_lata_380g.webp',
        '765g': '/images/productos/retail/Tomate_pelado_lata_765g.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-picante-pe.webp'
    },
    {
      id: 'tomate-frito',
      name: 'Tomate Frito',
      shortName: 'Tomate Frito',
      category: {
        es: 'Tomates en Conserva',
        en: 'Canned Tomatoes',
        fr: 'Tomates en conserve',
        pt: 'Tomates em conserva',
      },
      categorySlug: 'tomates-conserva',
      shortDescription: {
        es: 'Salsa de tomate lista para usar con sabor casero.',
        en: 'Ready-to-use tomato sauce with a homemade flavour.',
        fr: 'Sauce tomate prête à l’emploi au goût maison.',
        pt: 'Molho de tomate pronto a usar com sabor caseiro.',
      },
      description: {
        es: 'Salsa de tomate lista para usar, con una textura suave y un sabor casero que facilita la preparación de platos rápidos y sabrosos',
        en: 'Ready-to-use tomato sauce with a smooth texture and a homemade flavour that makes it easy to prepare quick, tasty dishes.',
        fr: 'Sauce tomate prête à l’emploi, à la texture douce et au goût maison, qui facilite la préparation de plats rapides et savoureux.',
        pt: 'Molho de tomate pronto a usar, com uma textura suave e um sabor caseiro que facilita a preparação de pratos rápidos e saborosos.',
      },
      ingredients: {
        es: 'Concentrado de tomate, aceite de girasol, azúcar, sal, almidón de maíz modificado, espesantes (goma guar, goma garrofín, pectinas), aromas (contiene soja), conservante (benzoato de sodio), especias.',
        en: 'Tomato concentrate, sunflower oil, sugar, salt, modified maize starch, thickeners (guar gum, locust bean gum, pectins), flavourings (contains soya), preservative (sodium benzoate), spices.',
        fr: 'Concentré de tomate, huile de tournesol, sucre, sel, amidon de maïs modifié, épaississants (gomme guar, gomme de caroube, pectines), arômes (contient du soja), conservateur (benzoate de sodium), épices.',
        pt: 'Concentrado de tomate, óleo de girassol, açúcar, sal, amido de milho modificado, espessantes (goma guar, goma de alfarroba, pectinas), aromas (contém soja), conservante (benzoato de sódio), especiarias.',
      },
      formats: ['350g'],
      formatDisplay: '350g',
      idealFor: {
        es: 'Pasta, arroces, carnes, pizzas y recetas rápidas del día a día.',
        en: 'Pasta, rice dishes, meat, pizzas and quick everyday recipes.',
        fr: 'Pâtes, riz, viandes, pizzas et recettes rapides du quotidien.',
        pt: 'Massa, arrozes, carnes, pizzas e receitas rápidas do dia a dia.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Tomate_frito_350g.webp',
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-picante-pe.webp'
    },
    {
      id: 'tomate-triturado',
      name: 'Tomate Triturado',
      shortName: 'Tomate Triturado',
      category: {
        es: 'Tomates en Conserva',
        en: 'Canned Tomatoes',
        fr: 'Tomates en conserve',
        pt: 'Tomates em conserva',
      },
      categorySlug: 'tomates-conserva',
      shortDescription: {
        es: 'Textura homogénea, perfecto como base para salsas.',
        en: 'Even texture, perfect as a base for sauces.',
        fr: 'Texture homogène, parfaite comme base pour les sauces.',
        pt: 'Textura homogénea, perfeito como base para molhos.',
      },
      description: {
        es: 'Tomate triturado de textura homogénea, perfecto como base para salsas, guisos y elaboraciones que necesitan un sabor intenso y natural.',
        en: 'Crushed tomato with an even texture, perfect as a base for sauces, stews and dishes that need an intense, natural flavour.',
        fr: 'Tomate concassée à la texture homogène, parfaite comme base pour les sauces, les ragoûts et les préparations qui demandent une saveur intense et naturelle.',
        pt: 'Tomate triturado de textura homogénea, perfeito como base para molhos, guisados e preparações que precisam de um sabor intenso e natural.',
      },
      ingredients: {
        es: 'Tomate triturado (99%), sal y acidulante ácido cítrico (Sólo se añade acidulante cuando se requiere corregir la acidez del tomate).',
        en: 'Crushed tomato (99%), salt and acidifier citric acid (Acidifier is only added when the acidity of the tomato needs to be corrected).',
        fr: 'Tomate concassée (99\u00a0%), sel et acidifiant acide citrique (L’acidifiant n’est ajouté que lorsqu’il est nécessaire de corriger l’acidité de la tomate).',
        pt: 'Tomate triturado (99%), sal e acidificante ácido cítrico (Só se adiciona acidificante quando é necessário corrigir a acidez do tomate).',
      },
      formats: ['400g', '800g'],
      formatDisplay: '400g | 800g',
      idealFor: {
        es: 'Salsas caseras, recetas de cuchara, guisos y bases de cocina.',
        en: 'Homemade sauces, hearty soups and stews, and cooking bases.',
        fr: 'Sauces maison, plats mijotés, ragoûts et bases de cuisine.',
        pt: 'Molhos caseiros, pratos de colher, guisados e bases de cozinha.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Tomate_triturado_lata_400g.webp',
      formatImages: {
        '400g': '/images/productos/retail/Tomate_triturado_lata_400g.webp',
        '800g': '/images/productos/retail/Tomate_triturado_lata_800g.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-picante-pe.webp'
    },
    {
      id: 'doble-concentrado-tomate',
      name: 'Doble Concentrado de Tomate',
      shortName: 'Doble Concentrado de Tomate',
      category: {
        es: 'Tomates en Conserva',
        en: 'Canned Tomatoes',
        fr: 'Tomates en conserve',
        pt: 'Tomates em conserva',
      },
      categorySlug: 'tomates-conserva',
      shortDescription: {
        es: 'Concentrado intenso para potenciar salsas y recetas.',
        en: 'Intense concentrate to boost sauces and recipes.',
        fr: 'Concentré intense pour relever sauces et recettes.',
        pt: 'Concentrado intenso para realçar molhos e receitas.',
      },
      description: {
        es: 'Un concentrado de tomate con sabor intenso, pensado para potenciar el color y la profundidad de salsas y recetas tradicionales.',
        en: 'A tomato concentrate with an intense flavour, designed to enhance the colour and depth of sauces and traditional recipes.',
        fr: 'Un concentré de tomate à la saveur intense, conçu pour rehausser la couleur et la profondeur des sauces et des recettes traditionnelles.',
        pt: 'Um concentrado de tomate com sabor intenso, pensado para realçar a cor e a profundidade de molhos e receitas tradicionais.',
      },
      ingredients: {
        es: 'Tomates.',
        en: 'Tomatoes.',
        fr: 'Tomates.',
        pt: 'Tomates.',
      },
      formats: ['140g', '400g'],
      formatDisplay: '140g | 400g',
      idealFor: {
        es: 'Salsas, estofados, marinados y recetas que requieren un toque extra de intensidad.',
        en: 'Sauces, stews, marinades and recipes that need an extra touch of intensity.',
        fr: 'Sauces, ragoûts, marinades et recettes qui demandent une touche d’intensité supplémentaire.',
        pt: 'Molhos, estufados, marinadas e receitas que pedem um toque extra de intensidade.',
      },
      productType: 'Retail',
      image: '/images/productos/retail/Concetrado_tomate_lata_140g.webp',
      formatImages: {
        '140g': '/images/productos/retail/Concetrado_tomate_lata_140g.webp',
        '400g': '/images/productos/HORECA/Concentrado_tomate_lata_400g.webp'
      },
      bannerImage: '/images/productos/BANNERS IDEAL PARA/ketchup-picante-pe.webp'
    }
  ]
};

/**
 * Obtiene todos los productos retail en un array plano
 * @returns {Array} Array con todos los productos
 */
export const getAllRetailProducts = () => {
  return [
    ...retailProductsData.ketchupCristal,
    ...retailProductsData.lineaPE,
    ...retailProductsData.alioliMayonesa,
    ...retailProductsData.tomates
  ];
};

/**
 * Busca un producto por su ID
 * @param {string} productId - ID del producto
 * @returns {Object|null} Producto encontrado o null
 */
export const getProductById = (productId) => {
  const allProducts = getAllRetailProducts();
  return allProducts.find(product => product.id === productId) || null;
};

/**
 * Obtiene productos por categoría
 * @param {string} categorySlug - Slug de la categoría
 * @returns {Array} Array de productos de la categoría
 */
export const getProductsByCategory = (categorySlug) => {
  const categoryMap = {
    'ketchup-cristal': retailProductsData.ketchupCristal,
    'linea-pe': retailProductsData.lineaPE,
    'alioli-mayonesa': retailProductsData.alioliMayonesa,
    'tomates-conserva': retailProductsData.tomates
  };
  return categoryMap[categorySlug] || [];
};
